import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { orderAPI, specialsAPI } from '../../api';
import AuthGate from '../AuthGate';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function CheckoutPage() {
  return (
    <AuthGate>
      <CheckoutPageContent />
    </AuthGate>
  );
}

function CheckoutPageContent() {
  const { user } = useAuth();
  const { cart, fetchCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [cardEnabled, setCardEnabled] = useState(false);
  const [idempotencyKey] = useState(() => {
    const existing = window.sessionStorage.getItem('petals-checkout-key');
    if (existing) return existing;
    const fresh = window.crypto.randomUUID();
    window.sessionStorage.setItem('petals-checkout-key', fresh);
    return fresh;
  });
  const [prebookDates, setPrebookDates] = useState([]);
  const [form, setForm] = useState({
    customer_name: '',
    customer_company: '',
    customer_email: '',
    customer_phone: '',
    delivery_method: 'pickup',
    delivery_date: '',
    order_type: 'regular',
    payment_method: 'bank_transfer',
    notes: '',
    shipping_address: { street: '', city: '', state: '', zip_code: '', country: '' }
  });

  useEffect(() => {
    if (user) {
      fetchCart();
      orderAPI.paymentMethods().then(res => setCardEnabled(Boolean(res.data.stripe))).catch(() => setCardEnabled(false));
      setForm(f => ({
        ...f,
        customer_name: user.buyer_name || '',
        customer_company: user.company_name || '',
        customer_email: user.email || '',
        customer_phone: user.phone || ''
      }));
    }
  }, [user, fetchCart]);

  useEffect(() => {
    if (form.order_type === 'prebook') {
      specialsAPI.getPrebookInfo().then(res => {
        setPrebookDates(res.data.available_dates || []);
      }).catch(() => {});
    }
  }, [form.order_type]);

  useEffect(() => {
    if (!cart?.items?.length || cart.checkout_blocked || cart.unavailable_items?.length) {
      setQuote(null);
      return;
    }
    let current = true;
    setQuote(null);
    setQuoteError('');
    const timer = setTimeout(() => {
      orderAPI.quoteCart({ delivery_method: form.delivery_method, shipping_address: form.shipping_address }).then(res => {
        if (current) setQuote(res.data);
      }).catch(err => {
        if (current) setQuoteError(formatApiError(err, 'Unable to calculate tax for this address'));
      });
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [cart, form.delivery_method, form.shipping_address]);

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setForm(f => ({ ...f, shipping_address: { ...f.shipping_address, [field]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quote) { toast.error(quoteError || 'A complete tax quote is required before checkout'); return; }
    if (cart?.unavailable_items?.length) { toast.error('Review unavailable cart items before ordering'); return; }
    if (!cart?.items?.length) { toast.error('Cart is empty'); return; }
    if (!form.customer_name || !form.customer_email) { toast.error('Please fill in required fields'); return; }
    setLoading(true);
    try {
      const res = await orderAPI.createOrder({ ...form, idempotency_key: idempotencyKey });
      if (form.payment_method === 'stripe' && res.data.checkout_url) {
        window.location.assign(res.data.checkout_url);
        return;
      }
      window.sessionStorage.removeItem('petals-checkout-key');
      toast.success('Order received successfully!');
      navigate(`/order-confirmation/${res.data.order._id}`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to place order'));
    } finally {
      setLoading(false);
    }
  };

  const items = cart?.items || [];

  return (
    <div className="pw-container py-2 pb-12">
      <Breadcrumbs items={[{ label: 'Cart', href: '/cart' }, { label: 'Checkout' }]} />
      <h1 className="text-xl font-semibold tracking-wider uppercase mb-6">Checkout</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <div className="border border-[#e7e3dd] rounded-xl p-6">
              <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Customer Information</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="checkout-name" className="text-xs text-[#6f6a63] block mb-1">Full Name *</label>
                  <input id="checkout-name" type="text" required value={form.customer_name} onChange={(e) => handleChange('customer_name', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                </div>
                <div>
                  <label htmlFor="checkout-company" className="text-xs text-[#6f6a63] block mb-1">Company</label>
                  <input id="checkout-company" type="text" value={form.customer_company} onChange={(e) => handleChange('customer_company', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                </div>
                <div>
                  <label htmlFor="checkout-email" className="text-xs text-[#6f6a63] block mb-1">Email *</label>
                  <input id="checkout-email" type="email" required value={form.customer_email} onChange={(e) => handleChange('customer_email', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                </div>
                <div>
                  <label htmlFor="checkout-phone" className="text-xs text-[#6f6a63] block mb-1">Phone</label>
                  <input id="checkout-phone" type="tel" value={form.customer_phone} onChange={(e) => handleChange('customer_phone', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                </div>
              </div>
            </div>

            {/* Delivery */}
            <div className="border border-[#e7e3dd] rounded-xl p-6">
              <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Delivery Method</h2>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <button type="button" onClick={() => handleChange('delivery_method', 'pickup')}
                  className={`p-3 border rounded-lg text-sm ${form.delivery_method === 'pickup' ? 'border-[#2f6b4f] bg-[#e7f1ea]' : 'border-[#e7e3dd]'}`}>
                  Pickup
                </button>
                <button type="button" onClick={() => handleChange('delivery_method', 'delivery')}
                  className={`p-3 border rounded-lg text-sm ${form.delivery_method === 'delivery' ? 'border-[#2f6b4f] bg-[#e7f1ea]' : 'border-[#e7e3dd]'}`}>
                  Delivery
                </button>
              </div>
              {form.delivery_method === 'delivery' && (
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="checkout-street" className="text-xs text-[#6f6a63] block mb-1">Street Address</label>
                    <input id="checkout-street" type="text" value={form.shipping_address.street} onChange={(e) => handleAddressChange('street', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                  </div>
                  <div>
                    <label htmlFor="checkout-city" className="text-xs text-[#6f6a63] block mb-1">City</label>
                    <input id="checkout-city" type="text" value={form.shipping_address.city} onChange={(e) => handleAddressChange('city', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                  </div>
                  <div>
                    <label htmlFor="checkout-state" className="text-xs text-[#6f6a63] block mb-1">State</label>
                    <input id="checkout-state" type="text" value={form.shipping_address.state} onChange={(e) => handleAddressChange('state', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                  </div>
                  <div>
                    <label htmlFor="checkout-postal" className="text-xs text-[#6f6a63] block mb-1">ZIP / Postal Code</label>
                    <input id="checkout-postal" type="text" value={form.shipping_address.zip_code} onChange={(e) => handleAddressChange('zip_code', e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                  </div>
                  <div>
                    <label htmlFor="checkout-country" className="text-xs text-[#6f6a63] block mb-1">Country Code (ISO 2 letters)</label>
                    <input id="checkout-country" type="text" maxLength={2} value={form.shipping_address.country} onChange={(e) => handleAddressChange('country', e.target.value.toUpperCase())} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
                  </div>
                </div>
              )}

            </div>

            {/* Payment */}
            <div className="border border-[#e7e3dd] rounded-xl p-6">
              <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Payment Method</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                <button type="button" onClick={() => handleChange('payment_method', 'bank_transfer')}
                  className={`p-4 border rounded-lg text-left ${form.payment_method === 'bank_transfer' ? 'border-[#2f6b4f] bg-[#e7f1ea]' : 'border-[#e7e3dd]'}`}>
                  <p className="text-sm font-medium">Bank Transfer</p>
                  <p className="text-xs text-[#6f6a63] mt-1">Pay via wire transfer</p>
                </button>
                <button type="button" onClick={() => handleChange('payment_method', 'skip_payment')}
                  className={`p-4 border rounded-lg text-left ${form.payment_method === 'skip_payment' ? 'border-[#2f6b4f] bg-[#e7f1ea]' : 'border-[#e7e3dd]'}`}>
                  <p className="text-sm font-medium">Skip Payment for Now</p>
                  <p className="text-xs text-[#6f6a63] mt-1">Direct agreement</p>
                </button>
                <button type="button" onClick={() => handleChange('payment_method', 'stripe')} disabled={!cardEnabled}
                  className={`p-4 border rounded-lg text-left ${form.payment_method === 'stripe' ? 'border-[#2f6b4f] bg-[#e7f1ea]' : 'border-[#e7e3dd]'} ${!cardEnabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <p className="text-sm font-medium">Credit Card (Stripe)</p>
                  <p className="text-xs text-[#6f6a63] mt-1">{cardEnabled ? 'Secure hosted checkout' : 'Currently unavailable'}</p>
                </button>
              </div>
            </div>

            {/* Notes */}
            <div className="border border-[#e7e3dd] rounded-xl p-6">
              <label htmlFor="checkout-notes" className="text-xs text-[#6f6a63] block mb-1">Order Notes (optional)</label>
              <textarea id="checkout-notes" value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] resize-none" placeholder="Special instructions..." />
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 border border-[#e7e3dd] rounded-xl p-6">
              <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Order Summary</h2>
              <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                {items.map(item => (
                  <div key={item.product_id} className="flex gap-2 text-sm">
                    <img src={item.product?.images?.[0] || item.product?.image_url || ''} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate">{item.product?.name}</p>
                      <p className="text-xs text-[#6f6a63]">{item.quantity} x ${item.unit_price?.toFixed(2)}</p>
                    </div>
                    <span className="text-xs font-medium">${item.line_total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2 text-sm border-t border-[#e7e3dd] pt-3">
                <div className="flex justify-between"><span className="text-[#6f6a63]">Subtotal</span><span>${cart.subtotal?.toFixed(2)}</span></div>
                {cart.volume_discount?.discount_amount > 0 && (
                  <div className="flex justify-between text-[#2f6b4f]"><span>Volume Discount</span><span>-${cart.volume_discount.discount_amount.toFixed(2)}</span></div>
                )}
                {cart.coupon_discount > 0 && (
                  <div className="flex justify-between text-[#2f6b4f]"><span>Coupon</span><span>-${cart.coupon_discount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-[#6f6a63]">Tax</span><span>{quote ? `$${quote.tax.toFixed(2)}` : 'Awaiting tax quote'}</span></div>
                <div className="border-t border-[#e7e3dd] pt-2 flex justify-between font-semibold text-lg">
                  <span>Total</span><span>{quote ? `$${quote.total.toFixed(2)}` : 'Unavailable'}</span>
                </div>
              </div>
              {quoteError && <p role="alert" className="mt-2 text-xs text-red-700">{quoteError}</p>}
              {cart?.coupon_error && <p role="alert" className="mt-2 text-xs text-red-700">{cart.coupon_error} Review your cart.</p>}

              <button
                data-testid="checkout-place-order-button"
                type="submit"
                disabled={loading || !items.length || !quote || !!cart.unavailable_items?.length || cart.checkout_blocked}
                className="w-full mt-6 bg-[#2d2d2d] text-white py-3.5 rounded-lg text-sm tracking-wider uppercase font-medium hover:bg-[#232323] transition-colors disabled:opacity-50 active:scale-[0.98]"
              >
                {loading ? 'Processing...' : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
